import { useEffect, useState } from 'react'
import type { Document } from '@/types'
import { documentService } from '@/services/documentService'

interface UseDocumentsResult {
  documents: Document[]
  loading: boolean
  error: string | null
  reload: () => Promise<void>
}

export function useDocuments(includeInactive = false): UseDocumentsResult {
  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true

    const load = async () => {
      try {
        setLoading(true)
        const docs = await documentService.getAllDocuments({ includeInactive })
        if (isMounted) {
          setDocuments(docs)
          setError(null)
        }
      } catch (err) {
        console.error('Failed to load documents', err)
        if (isMounted) {
          setError('Failed to load documents')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    load()
    const unsubscribe = documentService.subscribe(docs => {
      if (!isMounted) return
      const filtered = includeInactive ? docs : docs.filter(doc => doc.active)
      setDocuments([...filtered].sort((a, b) => a.sortOrder - b.sortOrder))
    })

    return () => {
      isMounted = false
      unsubscribe()
    }
  }, [includeInactive])

  const reload = async () => {
    await documentService.reloadFromRemote()
    const docs = await documentService.getAllDocuments({ includeInactive })
    setDocuments(docs)
  }

  return { documents, loading, error, reload }
}

export const documentTypeConfig = {
  TDS: {
    color: 'blue',
    icon: '📋',
    priority: 1,
  },
  ESR: {
    color: 'green',
    icon: '✅',
    priority: 2,
  },
  MSDS: {
    color: 'red',
    icon: '⚠️',
    priority: 8,
  },
  LEED: {
    color: 'emerald',
    icon: '🌿',
    priority: 6,
  },
  Installation: {
    color: 'orange',
    icon: '🔧',
    priority: 3,
  },
  warranty: {
    color: 'purple',
    icon: '🛡️',
    priority: 4,
  },
  Acoustic: {
    color: 'indigo',
    icon: '🔊',
    priority: 7,
  },
  PartSpec: {
    color: 'gray',
    icon: '📐',
    priority: 5,
  },
};
